/**
 * Cross-tab notification that a schedule draft changed.
 *
 * Its own module so subscribers do not have to import the draft manager to listen.
 */
import { useEffect } from "react";

import { reportError } from "utilities/errorHelpers";

const DRAFT_CHANGE_CHANNEL = "scheduleDraftChange";

/**
 * Callers pass async handlers (refetching draft state), but a BroadcastChannel listener cannot
 * await one - so the promise is explicitly accepted and its rejection routed to reportError rather
 * than left to surface as an unhandled rejection.
 */
export function useDraftChangeListener(callback: (event: MessageEvent) => void | Promise<void>) {
    useEffect(() => {
        const channel = new BroadcastChannel(DRAFT_CHANGE_CHANNEL);

        const handleMessage = (event: MessageEvent) => {
            void Promise.resolve(callback(event)).catch((error: unknown) => {
                reportError(error);
            });
        };

        channel.addEventListener("message", handleMessage);

        return () => {
            channel.removeEventListener("message", handleMessage);
            channel.close();
        };
    }, [callback]);
}

export function fireDraftChangeEvent() {
    const channel = new BroadcastChannel(DRAFT_CHANGE_CHANNEL);
    channel.postMessage({ type: "draftChange" });
    channel.close();
}
